import * as T from "common/src/bindings/Translation2";
import * as React from "react";
import {
  Provider as TftSetProvider,
  fromNum,
  use as useSet,
} from "common/src/data/TftSet.bs.js";
import { make as SetSelect } from "../components/SetSelect.bs.js";
import AdLayout from "../app-core/AdLayout";
import {
  Tabs,
  Tab,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  CircularProgress,
  IconButton,
} from "@mui/material";
import * as Sd from "common/src/tft-static/Sd";
import * as TraitImage from "common/src/tft-static/TraitImage";
import HexagonIcon from "@mui/icons-material/Hexagon";
import { make as UnitTooltip } from "common/src/tft-static/UnitTooltip.bs";
import UnitImage from "common/src/tft-static/UnitImage";
import FullSearch, { getCompsOptions } from "../components/FullSearch";
import { make as TraitsRow } from "common/src/components/TraitsRow.bs.js";
import { make as Tooltip } from "common/src/components/Tooltip.bs.js";
import { generateBoard as generateBoard10 } from "../team-planner10/TbUtils10.bs";
import { generateBoard as generateBoard11 } from "../team-planner11/TbUtils11.bs";
import { generateBoard as generateBoard12 } from "../team-planner12/TbUtils12.bs";
import { generateBoard as generateBoard13 } from "../team-planner13/TbUtils13.bs";
import { generateBoard as generateBoard14 } from "../team-planner14/TbUtils14.bs";
import { generateBoard as generateBoard46 } from "../team-planner46/TbUtils46.bs";
import { generateBoard as generateBoard15 } from "../team-planner15/TbUtils15.bs";
import { use as useScreenSize } from "common/src/atoms/ScreenAtom.bs";
import { useWindowHeight } from "../ctx/ScreenCtx.bs";
import { useWindowVirtualizer } from "@tanstack/react-virtual";

const generateBoard = (unitIds: Array<string>, set: number) => {
  switch (set) {
    case 10:
    case 100:
      return generateBoard10(unitIds);
    case 110:
      return generateBoard11(unitIds);
    case 120:
      return generateBoard12(unitIds);
    case 130:
      return generateBoard13(unitIds);
    case 140:
      return generateBoard14(unitIds);
    case 150:
      return generateBoard15(unitIds);
    case 46:
      return generateBoard46(unitIds);
    default:
      return "";
  }
};

type TeamTyp = [Array<string>, Array<[string, number]>, number];

const Team = ({
  team,
  traitTracker,
}: {
  team: TeamTyp;
  traitTracker: boolean;
}) => {
  let set = useSet();
  const screenSize = useScreenSize();
  let [units, traits, _totCost] = team;
  if (traitTracker) {
    traits = traits.filter(
      ([t, _]) => !Sd.isTraitUnique(t, set) || t === "TFT12_Druid",
    );
  } else {
    traits = traits.filter(([t, _]) => !Sd.isTraitUnique(t, set));
  }

  const board = React.useMemo(() => generateBoard(units, set), [units, set]);

  return (
    <div className="bg-bg2 p-2 pt-1 md:p-3 md:pt-1 h-[94px] mt-[6px]">
      <div className="flex items-center justify-between pb-[2px]">
        <div>
          <TraitsRow
            traits={traits}
            small={screenSize > 3}
            className="ml-auto"
          />
        </div>
        <div className="mr-[-2px]">
          <a target="_blank" href={`/team-builder/${board}`}>
            <Tooltip title={"Open in Team Builder"}>
              <IconButton size="small" onClick={(_) => {}} color="primary">
                <HexagonIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </a>
        </div>
      </div>
      <div className="flex items-center mx-[-2px]">
        {units.map((unitId: string) => (
          <UnitTooltip unit={unitId} key={unitId}>
            <div className="px-[2px] md:px-[3px]">
              {" "}
              <UnitImage size={32} sizeMd={40} unitId={unitId} />{" "}
            </div>
          </UnitTooltip>
        ))}
      </div>
    </div>
  );
};

type ListProps = { data: Array<any>; tab: number; traitTracker: boolean };

const getColCount = (screenSize: number, lvlTab: number) => {
  switch (screenSize) {
    case 0:
    case 1:
    case 2:
    case 3: {
      if (lvlTab <= 4) {
        return 4;
      } else if (lvlTab <= 6) {
        return 3;
      } else if (lvlTab <= 9) {
        return 2;
      } else {
        return 1;
      }
    }

    case 4:
    case 5:
    default: {
      if (lvlTab <= 4) {
        return 2;
      } else {
        return 1;
      }
    }
  }
};

const gridClass = (screenSize: number, lvlTab: number) => {
  switch (screenSize) {
    case 0:
    case 1:
    case 2:
    case 3: {
      if (lvlTab <= 4) {
        return "grid-cols-4";
      } else if (lvlTab <= 6) {
        return "grid-cols-3";
      } else if (lvlTab <= 9) {
        return "grid-cols-2";
      } else {
        return "grid-cols-1";
      }
    }

    case 4:
    case 5: {
      if (lvlTab <= 4) {
        return "grid-cols-2";
      } else {
        return "grid-cols-1";
      }
    }
  }
};

const List = ({ data, tab, traitTracker }: ListProps) => {
  const screenSize = useScreenSize();
  const wh = useWindowHeight();
  const colCount = getColCount(screenSize, tab);

  const bodyRef = React.useRef<null | HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = React.useState(500);
  const rowVirtualizer = useWindowVirtualizer({
    count: Math.ceil(data.length / colCount),
    estimateSize: () => 102,
    scrollMargin: scrollMargin,
  });

  React.useLayoutEffect(() => {
    if (bodyRef.current) {
      const rect = bodyRef.current.getBoundingClientRect();
      setScrollMargin(rect.top + window.scrollY);
    }
  }, [screenSize]);

  return (
    <div
      className="relative"
      style={{
        height: `${rowVirtualizer.getTotalSize()}px`,
      }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualItem) => {
        const team = data[virtualItem.index];
        return (
          <div
            key={virtualItem.key}
            className="flex justify-evenly"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start - scrollMargin}px)`,
            }}
          >
            {data
              .slice(
                virtualItem.index * colCount,
                virtualItem.index * colCount + colCount,
              )
              .map((team, idx) => (
                <Team key={idx} team={team} traitTracker={traitTracker} />
              ))}
          </div>
        );
      })}
    </div>
  );
};

const getTraits = (
  unitIds: any,
  extraTraits: any,
  set: any,
  // unitIds: Array<string>,
  // extraTraits: Array<string>,
  // set: number,
) => {
  const traitCounts: { [traitId: string]: number } = {};
  for (const unitId of unitIds) {
    for (const traitId of Sd.getUnitTraitsFull(unitId, set)) {
      traitCounts[traitId] = (traitCounts[traitId] || 0) + 1;
    }
  }

  for (const traitId of extraTraits) {
    traitCounts[traitId] = (traitCounts[traitId] || 0) + 1;
  }

  const traits = [];
  for (const [traitId, count] of Object.entries(traitCounts)) {
    const rank = Sd.traitRank(traitId, count, set) || 0;
    if (rank > 0) {
      traits.push([traitId, rank]);
    }
  }

  traits.sort(([t1, r1]: any, [t2, r2]: any) => {
    // @ts-ignore
    const c1 =
      (Sd.traitRankUnitCount(t2, r2, set) || 0) -
      (Sd.traitRankUnitCount(t1, r1, set) || 0);
    if (c1 !== 0) {
      return c1;
    } else {
      // @ts-ignore
      return Sd.traitStyle(t2, r2, set) - Sd.traitStyle(t1, r1, set);
    }
  });

  return traits;
};

const getParsedData = (
  data: Array<Array<string>>,
  traitFilter: any,
  set: number,
) => {
  return data.map((units) => [
    units,
    getTraits(units, traitFilter != null ? [traitFilter] : [], set),
    units.map((u) => Sd.unitCost(u, set)).reduce((acc, v) => acc + v, 0),
  ]);
};

const getFilteredData = (
  // data: Array<[Array<string>, Array<[string, number]>, number]>,
  // traitFilter: any,
  data: any,
  traitFilter: any,
  includeFilters: any,
  excludeFilters: any,
  exclude5Costs: boolean,
  set: number,
) => {
  let filteredData;
  if (exclude5Costs) {
    filteredData = data.filter(
      ([units]: any) => !units.some((u: any) => Sd.unitTier(u) == 5),
    );
  } else {
    filteredData = data;
  }

  filteredData = filteredData.filter(([units, traits, _]: any) => {
    return (
      includeFilters.every((filter: any) => {
        switch (filter.typ) {
          case "u":
            return units.some((u: any) => u === filter.id);
          case "t":
            return traits.some(
              ([t, r]: any) => t === filter.id && r === filter.rank,
            );
          default:
            return true;
        }
      }) &&
      !excludeFilters.some((filter: any) => {
        switch (filter.typ) {
          case "u":
            return units.some((u: any) => u === filter.id);
          case "t":
            return traits.some(
              ([t, r]: any) => t === filter.id && r === filter.rank,
            );
          default:
            return true;
        }
      })
    );
  });

  return filteredData;
};

const getSorting = (sorting: any, set: number) => {
  switch (sorting) {
    case "CostAsc":
      return ([_1, _2, c1]: any, [_3, _4, c2]: any) => c1 - c2;
    case "CostDesc":
      return ([_1, _2, c1]: any, [_3, _4, c2]: any) => c2 - c1;
    case "VerticalSynergies":
      return ([_1, ts1, _2]: any, [_3, ts2, _4]: any) => {
        const s1 = ts1.reduce((acc: number, [t, r]: any) => {
          let c = Sd.traitRankUnitCount(t, r, set) || 0;
          return acc + c * c;
        }, 0);
        const s2 = ts2.reduce((acc: number, [t, r]: any) => {
          let c = Sd.traitRankUnitCount(t, r, set) || 0;
          return acc + c * c;
        }, 0);
        return s2 - s1;
      };
    case "ActiveSynergies":
      return ([_1, ts1, _2]: any, [_3, ts2, _4]: any) => {
        const v2 = ts2.filter(
          ([t, _]: any) => !Sd.isTraitUnique(t, set),
        ).length;
        const v1 = ts1.filter(
          ([t, _]: any) => !Sd.isTraitUnique(t, set),
        ).length;
        return v2 - v1;
      };
  }
};

const Inner = ({
  data, // array<array<string>>,
  exclude5Costs,
  includeFilters,
  excludeFilters,
  traitFilter,
  sorting,
  tab,
  traitTracker,
}: any) => {
  const set = useSet();

  const parsedData = React.useMemo(() => {
    return getParsedData(data, traitFilter, set);
  }, [data, traitFilter, set]);

  const filteredData = React.useMemo(() => {
    // @ts-ignore
    let fd = getFilteredData(
      parsedData,
      traitFilter,
      includeFilters,
      excludeFilters,
      exclude5Costs,
      set,
    );
    if (traitTracker) {
      fd = fd.filter(
        ([_, traits, __]: any) =>
          traits.filter(
            ([tid]: [string]) =>
              !Sd.isTraitUnique(tid, set) || tid === "TFT12_Druid",
          ).length >= 8,
      );
    }
    return fd;
  }, [
    parsedData,
    sorting,
    traitFilter,
    includeFilters,
    excludeFilters,
    exclude5Costs,
    traitTracker,
  ]);

  const sortedData = React.useMemo(() => {
    const sortFn = getSorting(sorting, set);
    return filteredData.sort(sortFn);
  }, [filteredData, sorting, set]);

  return (
    <>
      <div className="pl-3 md:pl-0 mt-5 mb-2">
        {`${sortedData.length} total compositions`}
      </div>
      <div className="ml-[-4px]">
        <List data={sortedData} tab={tab} traitTracker={traitTracker} />
      </div>
    </>
  );
};

const tabStyle = "text-[13px] min-w-[58px] px-3 py-2";

const usePerfectSynergies = (
  set: number,
  ut: number,
  lvl: number,
  traitId: null | string,
) => {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setData(null);
    const setStr = set == 10 ? 100 : set;
    let url = `https://d3.tft.tools/perfect-synergies/${setStr}/${ut}/${lvl}/${
      traitId || "base"
    }`;
    fetch(url)
      .then((resp) => resp.json())
      .then((json) => {
        if (!cancelled) {
          setData(json);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [set, ut, lvl, traitId]);

  return data;
};

const Filter = ({ filter }: any) => {
  switch (filter.typ) {
    case "u":
      return <UnitImage unitId={filter.id} size={28} />;
    case "t":
      return (
        <TraitImage.PlainIcon2
          traitId={filter.id}
          rank={filter.rank}
          size={28}
        />
      );
    default:
      return null;
  }
};

const InnerWrap = () => {
  const set = useSet();

  const [unusedTraits, setUnusedTraits] = React.useState(0);
  const [exclude5Costs, setExclude5Costs] = React.useState(false);
  const [traitTracker, setTraitTracker] = React.useState(false);
  const [includeFilters, setIncludeFilters] = React.useState([]);
  const [excludeFilters, setExcludeFilters] = React.useState([]);

  const [lvlTab, setLvlTab] = React.useState(7);
  const [traitFilter, setTraitFilter] = React.useState<string | null>(null);
  const [sorting, setSorting] = React.useState("CostAsc");

  const data = usePerfectSynergies(
    set,
    traitTracker ? 2 : unusedTraits,
    lvlTab,
    traitFilter,
  );

  let searchOptions = React.useMemo(() => getCompsOptions(set), []);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end pl-3 md:pl-[6px] pt-2">
        <div className="pb-2 md:pb-1 flex items-center">
          <FormControl variant="standard">
            <InputLabel> {T.common("Unused Traits")} </InputLabel>
            <Select
              label={T.common("Unused Traits")}
              classes={{
                select: "text-[14px] w-[124px]",
              }}
              size="small"
              variant="standard"
              value={traitTracker ? 2 : unusedTraits}
              onChange={(e) => setUnusedTraits(Number(e.target.value))}
            >
              <MenuItem value={0}> {`0`} </MenuItem>
              <MenuItem value={1}> {`1 or less`} </MenuItem>
              <MenuItem value={2}> {`2 or less`} </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="pb-2 md:pb-1 md:pl-6">
          <FormControlLabel
            classes={{
              label: "text-[15px]",
            }}
            label={T.common("Exclude 5 Costs")}
            control={
              <Switch
                size="small"
                onChange={(_) => setExclude5Costs((t) => !t)}
                checked={exclude5Costs}
              />
            }
          />
        </div>
        <div className="pb-2 md:pb-0 md:pl-2 pr-4">
          <FormControl variant="standard">
            <InputLabel> {T.common("Trait +1 (Heart/Emblem)")} </InputLabel>
            <Select
              label={T.common("Trait +1 (Heart/Emblem)")}
              classes={{
                select: "text-[14px] w-[124px]",
              }}
              size="small"
              variant="standard"
              value={traitFilter || "-"}
              onChange={(e) => {
                setTraitFilter(e.target.value === "-" ? null : e.target.value);
              }}
            >
              <MenuItem value="-"> {`None`} </MenuItem>
              {Sd.allTraits(set)
                .filter((t) => !Sd.isTraitUnique(t, set))
                .map((traitId) => (
                  <MenuItem key={traitId} value={traitId}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <TraitImage.PlainIcon traitId={traitId} size={26} />
                      </div>
                      <div className="pl-1"> {T.traits(traitId)} </div>
                    </div>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </div>
        <div className="md:pl-2">
          <FormControl variant="standard">
            <InputLabel> {T.common("Sorting")} </InputLabel>
            <Select
              label={T.common("Sorting")}
              classes={{
                select: "text-[14px] w-[124px]",
              }}
              size="small"
              variant="standard"
              value={sorting}
              onChange={(e) => setSorting(e.target.value)}
            >
              <MenuItem value="CostAsc"> {T.common("Lowest Cost")} </MenuItem>
              <MenuItem value="CostDesc"> {T.common("Highest Cost")} </MenuItem>
              <MenuItem value="VerticalSynergies">
                {T.common("Highest Trait Ranks")}
              </MenuItem>
              <MenuItem value="ActiveSynergies">
                {T.common("Most Active Synergies")}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="pb-2 md:pb-1 md:pl-6">
          <FormControlLabel
            classes={{
              label: "text-[15px]",
            }}
            label={T.augments("TraitTracker")}
            control={
              <Switch
                size="small"
                onChange={(_) => setTraitTracker((t) => !t)}
                checked={traitTracker}
              />
            }
          />
        </div>
      </div>
      <div className="flex pl-3 md:pl-0 pt-3">
        <div className="flex gap-2 items-center flex-1">
          <div>{T.common("Filters") + ":"}</div>
          <div className="flex gap-1">
            {includeFilters.map((filter, idx) => (
              <div
                key={idx}
                className="cursor-pointer"
                onClick={(_) =>
                  setIncludeFilters((fs) =>
                    fs.slice(0, idx).concat(fs.slice(idx + 1)),
                  )
                }
              >
                <Filter filter={filter} />
              </div>
            ))}
          </div>
          <FullSearch
            placeholder={T.common("Add Filter")}
            // @ts-ignore
            onChange={(v) => setIncludeFilters((fs) => fs.concat([v]))}
            options={searchOptions}
          />
        </div>
        <div className="flex gap-2 items-center flex-1">
          <div>{T.common("Exclude") + ":"}</div>
          <div className="flex gap-1">
            {excludeFilters.map((filter, idx) => (
              <div
                key={idx}
                className="cursor-pointer"
                onClick={(_) =>
                  setExcludeFilters((fs) =>
                    fs.slice(0, idx).concat(fs.slice(idx + 1)),
                  )
                }
              >
                <Filter filter={filter} />
              </div>
            ))}
          </div>
          <FullSearch
            placeholder={T.common("Add Filter")}
            // @ts-ignore
            onChange={(v) => setExcludeFilters((fs) => fs.concat([v]))}
            options={searchOptions}
          />
        </div>
      </div>
      <div className="pt-[10px]">
        <Tabs value={lvlTab} onChange={(_, t) => setLvlTab(t)}>
          <Tab
            label="Lvl 4"
            value={4}
            classes={{
              root: tabStyle,
            }}
          />
          <Tab
            label="Lvl 5"
            value={5}
            classes={{
              root: tabStyle,
            }}
          />
          <Tab
            label="Lvl 6"
            value={6}
            classes={{
              root: tabStyle,
            }}
          />
          <Tab
            label="Lvl 7"
            value={7}
            classes={{
              root: tabStyle,
            }}
          />
          <Tab
            label="Lvl 8"
            value={8}
            classes={{
              root: tabStyle,
            }}
          />
          <Tab
            label="Lvl 9"
            value={9}
            classes={{
              root: tabStyle,
            }}
          />
          <Tab
            label="Lvl 10"
            value={10}
            classes={{
              root: tabStyle,
            }}
          />
        </Tabs>
      </div>
      {data != null ? (
        <Inner
          data={data}
          exclude5Costs={exclude5Costs}
          traitTracker={traitTracker}
          includeFilters={includeFilters}
          excludeFilters={excludeFilters}
          traitFilter={traitFilter}
          sorting={sorting}
          tab={lvlTab}
        />
      ) : (
        <div className="w-full py-16 flex items-center justify-center p-4">
          <CircularProgress size={60} />
        </div>
      )}
    </div>
  );
};

export default function PerfectSynergiesPage() {
  const [set, setSet] = React.useState(() => fromNum(150));

  return (
    <AdLayout>
      <div className="bg-bg p-2 md:p-4 w-[380px] md:w-[928px] min-h-[700px]">
        <div className="flex items-center justify-between">
          <h2 className="pl-2 pt-2 pb-1 text-xl font-montserrat font-medium">
            {T.common("Perfect Synergies")}
          </h2>
          <SetSelect set={set} setSet={setSet} />
        </div>
        <TftSetProvider.make value={set}>
          <div>
            <InnerWrap key={set} />
          </div>
        </TftSetProvider.make>
      </div>
    </AdLayout>
  );
}
